import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, X, Layers, Pencil } from "lucide-react";

const PRIORITIES = [
  { value: "niedrig", label: "Niedrig" },
  { value: "mittel", label: "Mittel" },
  { value: "hoch", label: "Hoch" },
];

const BOARDS = [
  { board_id: "avgs", label: "AVGS-Coaches" },
  { board_id: "we_academy", label: "We Academy Team" },
];

const emptyForm = () => ({
  board_id: "avgs", title: "", description: "", category: "", priority: "mittel",
  due_offset_days: "", checklists: [{ title: "Checkliste", items: [""] }], active: true,
});

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    try {
      const r = await api.get("/boards/templates", { params: { include_inactive: true } });
      setTemplates(r.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateChecklist = (idx, patch) => {
    setForm((f) => ({ ...f, checklists: f.checklists.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  };
  const updateItem = (clIdx, itemIdx, value) => {
    setForm((f) => ({
      ...f,
      checklists: f.checklists.map((c, i) => (i === clIdx ? { ...c, items: c.items.map((it, j) => (j === itemIdx ? value : it)) } : c)),
    }));
  };
  const addItem = (clIdx) => updateChecklist(clIdx, { items: [...form.checklists[clIdx].items, ""] });
  const removeItem = (clIdx, itemIdx) => updateChecklist(clIdx, { items: form.checklists[clIdx].items.filter((_, j) => j !== itemIdx) });
  const addChecklist = () => setForm((f) => ({ ...f, checklists: [...f.checklists, { title: "Weitere Checkliste", items: [""] }] }));
  const removeChecklist = (idx) => setForm((f) => ({ ...f, checklists: f.checklists.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titel fehlt"); return; }
    const body = {
      ...form,
      due_offset_days: form.due_offset_days === "" ? null : Number(form.due_offset_days),
      checklists: form.checklists.map((c) => ({ title: c.title, items: c.items.filter((i) => i.trim()) })).filter((c) => c.items.length),
    };
    try {
      if (editingId) {
        await api.patch(`/boards/templates/${editingId}`, body);
        toast.success("Vorlage aktualisiert");
      } else {
        await api.post("/boards/templates", body);
        toast.success("Vorlage erstellt");
      }
      setForm(emptyForm());
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const startEdit = (tpl) => {
    setForm({
      board_id: tpl.board_id || "avgs",
      title: tpl.title || "",
      description: tpl.description || "",
      category: tpl.category || "",
      priority: tpl.priority || "mittel",
      due_offset_days: tpl.due_offset_days ?? "",
      checklists: (tpl.checklists && tpl.checklists.length ? tpl.checklists : [{ title: "Checkliste", items: [""] }])
        .map((c) => ({ title: c.title, items: [...c.items, ""] })),
      active: tpl.active !== false,
    });
    setEditingId(tpl.template_id);
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setForm(emptyForm()); setEditingId(null); };

  const toggleActive = async (tpl) => {
    try { await api.patch(`/boards/templates/${tpl.template_id}`, { active: !tpl.active }); load(); } catch (e) { toast.error(e.message); }
  };
  const duplicate = async (tpl) => {
    try { await api.post(`/boards/templates/${tpl.template_id}/duplicate`); toast.success("Vorlage dupliziert"); load(); } catch (e) { toast.error(e.message); }
  };
  const remove = async (tpl) => {
    if (!window.confirm(`Vorlage „${tpl.title}“ wirklich löschen?`)) return;
    try { await api.delete(`/boards/templates/${tpl.template_id}`); load(); } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="text-xs font-bold uppercase tracking-overline text-slate-500 py-8">Lädt Vorlagen…</div>;

  return (
    <div data-testid="template-manager">
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm text-slate-500 flex items-center gap-2"><Layers size={14} strokeWidth={1.5} /> Kartenvorlagen, die beim Erstellen einer Karte im Board wählbar sind.</div>
        {!showForm && (
          <button onClick={() => { setForm(emptyForm()); setEditingId(null); setShowForm(true); }} data-testid="template-new-button" className="flex items-center gap-1.5 bg-slate-900 text-white text-xs font-medium px-3 py-2 hover:bg-black">
            <Plus size={13} /> Neue Vorlage
          </button>
        )}
      </div>

      {showForm && (
        <div className="border border-slate-200 bg-white p-5 mb-6 space-y-4" data-testid="template-form">
          <div className="flex gap-3 flex-wrap">
            <select value={form.board_id} onChange={(e) => setForm({ ...form, board_id: e.target.value })} className="input-tech text-xs" data-testid="template-board-select">
              {BOARDS.map((b) => <option key={b.board_id} value={b.board_id}>{b.label}</option>)}
            </select>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel der Vorlage"
              className="input-tech text-xs flex-1 min-w-[200px]" data-testid="template-title-input" />
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
            placeholder="Beschreibung" className="w-full border border-slate-200 p-2 text-xs focus:outline-none focus:border-slate-400" data-testid="template-description-input" />
          <div className="flex gap-3 flex-wrap">
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Kategorie (z. B. Onboarding)"
              className="input-tech text-xs" data-testid="template-category-input" />
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input-tech text-xs" data-testid="template-priority-select">
              {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <input type="number" value={form.due_offset_days} onChange={(e) => setForm({ ...form, due_offset_days: e.target.value })}
              placeholder="Frist in Tagen ab Erstellung" className="input-tech text-xs w-56" data-testid="template-due-offset-input" />
          </div>

          <div className="space-y-4">
            {form.checklists.map((cl, clIdx) => (
              <div key={clIdx} className="border border-slate-100 bg-slate-50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <input value={cl.title} onChange={(e) => updateChecklist(clIdx, { title: e.target.value })}
                    className="flex-1 border border-slate-200 p-1.5 text-xs font-medium focus:outline-none focus:border-slate-400"
                    data-testid={`template-checklist-title-${clIdx}`} />
                  <button onClick={() => removeChecklist(clIdx)} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
                {cl.items.map((it, itemIdx) => (
                  <div key={itemIdx} className="flex items-center gap-2 mb-1.5">
                    <input value={it} onChange={(e) => updateItem(clIdx, itemIdx, e.target.value)} placeholder="Checklisten-Punkt"
                      className="flex-1 border border-slate-200 p-1.5 text-xs focus:outline-none focus:border-slate-400"
                      data-testid={`template-checklist-item-${clIdx}-${itemIdx}`} />
                    <button onClick={() => removeItem(clIdx, itemIdx)} className="text-slate-400 hover:text-red-600"><X size={13} /></button>
                  </div>
                ))}
                <button onClick={() => addItem(clIdx)} data-testid={`template-add-item-${clIdx}`} className="text-[11px] text-[#00807E] hover:underline mt-1">+ Punkt hinzufügen</button>
              </div>
            ))}
            <button onClick={addChecklist} data-testid="template-add-checklist" className="text-xs font-medium text-slate-600 hover:underline flex items-center gap-1">
              <Plus size={12} /> Checkliste hinzufügen
            </button>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={save} data-testid="template-save-button" className="bg-slate-900 text-white text-xs font-medium px-4 py-2 hover:bg-black">
              {editingId ? "Änderungen speichern" : "Vorlage speichern"}
            </button>
            <button onClick={cancelForm} data-testid="template-cancel-button" className="text-xs text-slate-500 px-2">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {templates.map((tpl) => (
          <div key={tpl.template_id} className={`border border-slate-200 bg-white p-4 flex items-center justify-between gap-4 ${!tpl.active ? "opacity-50" : ""}`} data-testid={`template-row-${tpl.template_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800">{tpl.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {BOARDS.find((b) => b.board_id === tpl.board_id)?.label || "Alle Boards"}
                {tpl.category ? ` · ${tpl.category}` : ""}{tpl.priority ? ` · Priorität: ${tpl.priority}` : ""}
                {" · "}{(tpl.checklists || []).reduce((n, c) => n + c.items.length, 0)} Checklisten-Punkte
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => toggleActive(tpl)} data-testid={`template-toggle-active-${tpl.template_id}`}
                className={`text-[10px] font-bold uppercase tracking-overline px-2 py-1 border ${tpl.active ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500"}`}>
                {tpl.active ? "Aktiv" : "Inaktiv"}
              </button>
              <button onClick={() => startEdit(tpl)} data-testid={`template-edit-${tpl.template_id}`} className="text-slate-500 hover:text-slate-900"><Pencil size={14} strokeWidth={1.5} /></button>
              <button onClick={() => duplicate(tpl)} data-testid={`template-duplicate-${tpl.template_id}`} className="text-slate-500 hover:text-slate-900"><Copy size={14} strokeWidth={1.5} /></button>
              <button onClick={() => remove(tpl)} data-testid={`template-delete-${tpl.template_id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} strokeWidth={1.5} /></button>
            </div>
          </div>
        ))}
        {templates.length === 0 && <div className="text-xs text-slate-400 italic text-center py-8">Noch keine Vorlagen angelegt.</div>}
      </div>
    </div>
  );
}
